import * as React from "react";

import {
  Copy,
  Button,
  Tooltip,
  Grid,
  Paper,
  Skeleton,
  TableContainer,
  Table,
  TableCell,
  TableHead,
  TableBody,
  TableRow,
  Typography,
  IconButton,
  Popover,
} from "mpulse-design-system";
import InfiniteScroll from "react-infinite-scroll-component";
import { alpha, Box, styled } from "@mui/material";
import copy from "clipboard-copy";
import InfoIcon from "@mui/icons-material/Info";

import { pluralize } from "../../utils/utils";
import AudioFileService from "../../services/AudioFile/AudioFileService";

import AudioStatusChip from "../AudioStatusChip/AudioStatusChip";
import UploadFiles from "../UploadFiles/UploadFiles";

const VTDashboard = () => {
  const [showUploadPopup, setShowUploadPopup] = React.useState(false);
  const [audioReady, setAudioReady] = React.useState({
    count: -1,
    next: null,
    data: [],
  });
  const [audioFileMap, setAudioFileMap] = React.useState({});

  const getColumnProps = (title, size) => {
    return { title, size };
  };

  const columnsMap = {
      publishDate: getColumnProps("Publish Date", "10%"),
      metadataName: getColumnProps("Dept. Name", "13%"),
      contentType: getColumnProps("Content Type", "11%"),
      content: getColumnProps("Content", "17%"),
      language: getColumnProps("Language", "10%"),
      fileName: getColumnProps("File Name", "20%"),
      priority: getColumnProps("Priority/Status", "19%"),
    },
    columns = Object.keys(columnsMap);

  const fetchData = (delay = 1500) => {
    setTimeout(() => {
      AudioFileService.getVTAudioFileInfo(audioReady.next).then(
        ({ count, next, results }) => {
          let data = audioReady.data;
          let audioFileMapTemp = audioFileMap;
          results.map((item, i) => {
            audioFileMapTemp[item.file_name] = {
              index: i,
              id: item.id,
            }; // Keeps a map of the index so we can check we're expecting that file when we upload
            const key = `${i}-${item.file_name}`,
              language =
                item.language && typeof item.language === "string"
                  ? item.language[0] + item.language.slice(1).toLowerCase()
                  : undefined;

            data.push(<AudioItem item={item} language={language} key={key} />);
          });

          setAudioReady({
            count,
            next,
            data,
          });
          setAudioFileMap(audioFileMapTemp);
        }
      );
    }, delay); // Small delay to prevent lag from loading next set
  };

  const removeAudioFilesFromList = (indexes) => {
    let data = audioReady.data;
    let count = audioReady.count;
    let prevIndex = null;
    indexes.map((i) => {
      data.splice(i, 1);
      count -= 1;
      prevIndex = i;
    });
    setAudioReady({
      next: audioReady.next,
      count,
      data,
    });
  };

  const handleShowUploadPopup = (e) => {
    e.preventDefault();
    setShowUploadPopup(true);
  };

  React.useEffect(() => {
    setAudioReady({
      count: -1,
      next: null,
      data: [],
    });
    fetchData(0);
  }, []);

  return (
    <Box sx={{ margin: (theme) => `${theme.spacing(5)} ${theme.spacing(3)}` }}>
      <Paper
        variant="elevation"
        elevation={0}
        square
        sx={{
          padding: (theme) => `${theme.spacing(2)} ${theme.spacing(3)}`,
          backgroundColor: "white",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <Typography color="textTertiary">
              {audioReady.count === -1 ? (
                <Skeleton width={115} />
              ) : (
                "Showing " + pluralize("item", { count: audioReady.count })
              )}
            </Typography>
          </Grid>
          {audioReady.count <= 0 ? null : (
            <Grid item container xxs={5} justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                size="medium"
                sx={{ justifySelf: "right" }}
                onClick={handleShowUploadPopup}
              >
                Upload audio
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>
      <InfiniteScroll
        next={fetchData}
        hasMore={audioReady.next}
        loader={<TableRowSkeleton colSpan={columns.length} />}
        dataLength={audioReady.data.length}
      >
        <TableContainer
          sx={{ borderRadius: "unset !important", boxSizing: "border-box" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, i) => {
                  return (
                    <StyledTableCell
                      key={i}
                      sx={{ width: columnsMap[column].size }}
                    >
                      {columnsMap[column].title}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {audioReady.count === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={columns.length}>
                    No Audio Needed.
                  </StyledTableCell>
                </TableRow>
              ) : (
                audioReady.data
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </InfiniteScroll>
      {showUploadPopup ? (
        <UploadFiles
          setPopupEnabled={setShowUploadPopup}
          audioFileMap={audioFileMap}
          removeAudioFilesFromList={removeAudioFilesFromList}
        />
      ) : null}
    </Box>
  );
};

const TableRowSkeleton = ({ colSpan }) => {
  return (
    <TableContainer
      sx={{
        borderRadius: "unset !important",
        boxSizing: "border-box",
        borderTop: "none !important",
      }}
    >
      <Table>
        <TableBody>
          <TableRow>
            <StyledTableCell colSpan={colSpan}>
              <Skeleton />
            </StyledTableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const AudioItem = ({ item, key, language }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleCopy = (e) => {
    copy(e.currentTarget.id);
  };

  const handleClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "rejection-reason" : undefined;

  return (
    <TableRow key={key}>
      <StyledTableCell key={`${key}-date`} sx={{ overflow: "initial" }}>
        {item.publish_date}
      </StyledTableCell>
      <StyledTableCell key={`${key}-metadataName`} sx={{ overflow: "initial" }}>
        {item.metadata_name}
      </StyledTableCell>
      <StyledTableCell key={`${key}-contentType`} sx={{ overflow: "initial" }}>
        {item.content_type}
      </StyledTableCell>
      <StyledTableCell key={`${key}-content`} sx={{ overflow: "initial" }}>
        <Grid container rowSpacing={1}>
          <Grid item xs={12}>
            {item.content}
          </Grid>
          {item.pronunciation ? (
            <Grid container item xs={12}>
              <Grid item xs={12}>
                <Typography color="textHint" variant="overline">
                  PRONUNCIATION NOTE:
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography color="textPrimary" variant="body1">
                  {item.pronunciation}
                </Typography>
              </Grid>
            </Grid>
          ) : null}
        </Grid>
      </StyledTableCell>
      <StyledTableCell key={`${key}-language`} sx={{ overflow: "initial" }}>
        {language}
      </StyledTableCell>
      <StyledTableCell key={`${key}-fileName`} sx={{ overflow: "initial" }}>
        <Box>
          <Box
            sx={{
              display: "inline",
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            {item.file_name}
          </Box>
          <Tooltip variant="dark" title="Copy file name" arrow>
            <Box
              id={item.file_name}
              sx={{
                display: "inline-block",
                cursor: "pointer",
                "&:hover": {
                  color: (theme) =>
                    alpha(
                      theme.palette.text.secondary,
                      theme.palette.action.hoverOpacity
                    ),
                },
              }}
              onClick={handleCopy}
            >
              <Copy
                titleAccess={null}
                sx={{
                  position: "relative",
                  top: 3,
                  left: 8,
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </StyledTableCell>
      <StyledTableCell key={`${key}-priority`} sx={{ overflow: "initial" }}>
        <span style={{ marginRight: "8px" }}>{item.priority}</span>
        <AudioStatusChip status={item.status} />{" "}
        {item.status === "REJECTED" ? (
          <Box sx={{ display: "inline-block" }}>
            <IconButton
              aria-describedby={id}
              aria-haspopup="true"
              onClick={handleClick}
            >
              <InfoIcon />
            </IconButton>
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              arrow="topRight"
            >
              <Typography sx={{ p: 2 }} color="textPrimary">
                {item.last_rejection_reason}
              </Typography>
            </Popover>
          </Box>
        ) : null}
      </StyledTableCell>
    </TableRow>
  );
};

const StyledTableCell = styled(TableCell)(() => ({
  "&.MuiTableCell-root": {
    whiteSpace: "unset",
    verticalAlign: "middle",
  },
}));

export default VTDashboard;
